import base64url from "base64url";
import { ExpiredParentWindowSession, UnknownFailure } from "./errors";
import {
  clearSessionState,
  getInflightRequest,
  getSignedRequestPayload,
  setInflightRequest,
  setSignedRequestPayload,
} from "./session-state";
import { getMockedResult, getMockedStatus, usingFakes } from "./testing";
import type { DecodedSignedRequest, SignedRequestContextUser } from "./types";

function reauthenticateCallback(
  resolve: (value: unknown) => void,
  reject: (data: unknown) => void,
  data: any,
): void {
  if (data.status < 400) {
    resolve(data);
    return;
  }
  if (data.status === 401) {
    // We 401'd an auth request - so the browser needs a refresh
    authentication.refreshCanvas();
    reject(new ExpiredParentWindowSession("Beginning refresh..."));
    return;
  }

  reject(new UnknownFailure(JSON.stringify(data)));
}

export const authentication = {
  attemptToReauthenticate(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (usingFakes()) {
        reauthenticateCallback(resolve, reject, createRawPayload(getMockedResult()));
      } else {
        Sfdc.canvas.client.refreshSignedRequest((data: any) => {
          return reauthenticateCallback(resolve, reject, data);
        });
      }
    });
  },

  clearSalesforceSessionDetails() {
    clearSessionState();
  },

  async refreshSignedRequest() {
    this.clearSalesforceSessionDetails();
    await getSalesforceSessionDetails();
  },

  async getWholeSignedRequest(): Promise<DecodedSignedRequest> {
    return getSalesforceSessionDetails();
  },

  async getSFUser(): Promise<SignedRequestContextUser> {
    const info = await getSalesforceSessionDetails();
    return info.context.user;
  },

  async getAccessToken() {
    const info = await getSalesforceSessionDetails();
    return info.client.oauthToken;
  },

  async getSFOrgURL() {
    const info = await getSalesforceSessionDetails();
    return info.client.instanceUrl;
  },

  refreshCanvas() {
    Sfdc.canvas.client.repost({ refresh: true });
  },

  async getClient() {
    const info = await getSalesforceSessionDetails();
    return info.client;
  },
};

async function getSalesforceSessionDetails(): Promise<DecodedSignedRequest> {
  const cached = getSignedRequestPayload();
  if (cached) {
    return cached;
  }

  // If a request is already in flight, all concurrent callers share the same
  // Promise so only one network request is ever made.
  const inflight = getInflightRequest();
  if (inflight) {
    return inflight;
  }

  const pending = fetchAndDecodeSignedRequest();
  setInflightRequest(pending);

  try {
    return await pending;
  } finally {
    setInflightRequest(null);
  }
}

async function fetchAndDecodeSignedRequest(): Promise<DecodedSignedRequest> {
  const result = await authentication.attemptToReauthenticate();

  const encodedPiece = result.payload.response.split(".")[1];
  let decoded: string | undefined;

  if (usingFakes()) {
    decoded = base64url.decode(encodedPiece);
  } else {
    decoded = Sfdc.canvas.decode(encodedPiece);
  }

  const raw: unknown = JSON.parse(decoded);
  const parsed = raw as DecodedSignedRequest;
  setSignedRequestPayload(parsed);
  return parsed;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createRawPayload(data: any) {
  return {
    status: getMockedStatus(),
    payload: {
      response: `12345.${base64url.encode(JSON.stringify(data))}`,
    },
  };
}
